import { intl, UUID } from '@beelday/common'
import styled from '@emotion/styled'
import { ReactNode } from 'react'
import {
	Action,
	CtaBar,
	Eyebrow,
	Hint,
	Option,
	Options,
	Progress,
	Question,
	QuestionImage,
	Screen,
} from './primitives'

const Back = styled.div`
	flex: 0 0 36%;
	display: flex;
`

type KnowledgeCheckOption = { id: UUID; text: string }

type Props = {
	index: number
	total: number
	question: string
	imageUrl?: string
	options: KnowledgeCheckOption[]
	multiple: boolean
	selected: UUID[]
	/** A step is on its way to the server; every control waits for it. */
	busy: boolean
	timer?: ReactNode
	onToggle: (id: UUID) => void
	onPrev: () => void
	onNext: () => void
	onFinish: () => void
}

/** One knowledge-check question per screen. */
export const KnowledgeCheckView = ({
	index,
	total,
	question,
	imageUrl,
	options,
	multiple,
	selected,
	busy,
	timer,
	onToggle,
	onPrev,
	onNext,
	onFinish,
}: Props): JSX.Element => {
	const isFirst = index === 0
	const isLast = index === total - 1

	return (
		<Screen $withCta data-testid="in_person.mobile.knowledge_check">
			{/* A fixed label, as in the mockup: the check's own name is the
			    trainer's working title and means little to a participant. */}
			<Eyebrow>
				<intl.Translate>
					in_person.mobile.knowledge_check.eyebrow
				</intl.Translate>
			</Eyebrow>
			<Progress
				label={
					<intl.Translate current={index + 1} total={total}>
						in_person.mobile.knowledge_check.progress
					</intl.Translate>
				}
				aside={timer}
				percent={(index / total) * 100}
			/>
			<Question>{question}</Question>
			{imageUrl ? <QuestionImage src={imageUrl} alt="" /> : null}
			<Options role={multiple ? 'group' : 'radiogroup'}>
				{options.map(option => (
					<Option
						key={option.id}
						multiple={multiple}
						selected={selected.includes(option.id)}
						disabled={busy}
						onClick={() => onToggle(option.id)}
					>
						{option.text}
					</Option>
				))}
			</Options>
			{multiple ? (
				<Hint>
					<intl.Translate>in_person.mobile.poll.multiple_hint</intl.Translate>
				</Hint>
			) : null}
			<CtaBar>
				{isFirst ? null : (
					<Back>
						<Action secondary disabled={busy} onClick={onPrev}>
							<intl.Translate>in_person.mobile.back</intl.Translate>
						</Action>
					</Back>
				)}
				{/* A question may be skipped, as on desktop: finishing with one
				    unanswered asks to confirm that (`useTraineeKnowledgeCheck`). */}
				<Action busy={busy} onClick={isLast ? onFinish : onNext}>
					<intl.Translate>
						{isLast
							? 'in_person.mobile.knowledge_check.finish'
							: 'in_person.mobile.next'}
					</intl.Translate>
				</Action>
			</CtaBar>
		</Screen>
	)
}

export default KnowledgeCheckView
